/**
 * Where a merged segment's text goes: start/end times, its note, and its label.
 *
 * Two layouts, chosen by whether the row is tall enough (the grid sizes rows so it is):
 *
 *   stacked  times on a top line, the label below; a note goes in its own lane under
 *            the row (see noteLane), or on the top line when the grid has no lane;
 *   inline   one line: [start] [note…] label [end] — the fallback for a row squeezed
 *            below two text lines, where the width does the work the height can't.
 *
 * Annotation text never drops below a legible floor. It used to be the label size
 * minus 5px — about 5px on a standard laptop screen, where each glyph gets five
 * device pixels and renders as blocks. Whatever doesn't fit at the floor is cut with
 * an ellipsis (the hover card shows it in full) rather than shrunk.
 *
 * Pure geometry: text widths come from callbacks, so this is testable without a canvas.
 */

/** Annotation (time/note) size for a given label size, never below `floorPx`. */
export function annotationFontSize(labelSize: number, floorPx: number): number {
  return Math.max(floorPx, Math.round(labelSize * 0.7));
}

/** Whether a segment is tall enough to stack an annotation line above its label. */
export function fitsStacked(segHeight: number, annSize: number, labelSize: number): boolean {
  return segHeight >= annSize + 2 + labelSize;
}

/**
 * Which of a block's start / end times fit on its top line. Both when the two labels and a
 * gap fit side by side; else the start alone; else neither (the hover card carries them).
 * Never overlaps two labels — they are dropped, not squeezed.
 */
export function fitTimes(
  width: number, startWidth: number, endWidth: number, opts: { gap?: number; pad?: number } = {},
): { showStart: boolean; showEnd: boolean } {
  const gap = opts.gap ?? 4;
  const room = width - (opts.pad ?? 4);
  if (startWidth > 0 && endWidth > 0 && startWidth + gap + endWidth <= room) return { showStart: true, showEnd: true };
  if (startWidth > 0 && startWidth <= room) return { showStart: true, showEnd: false };
  return { showStart: false, showEnd: false };
}

/** Cut `text` with an ellipsis to fit `maxWidth`, or '' when fewer than two characters
 *  would survive — a lone "B…" says nothing a marker doesn't. */
export function ellipsize(text: string, maxWidth: number, measure: (s: string) => number): string {
  if (measure(text) <= maxWidth) return text;
  let cut = text;
  while (cut.length > 0 && measure(cut + '…') > maxWidth) cut = cut.slice(0, -1);
  return cut.trimEnd().length >= 2 ? cut.trimEnd() + '…' : '';
}

export interface InlineInput {
  x: number;
  width: number;
  /** Widths of the start/end time labels, 0 when they are not to be shown. */
  startWidth: number;
  endWidth: number;
  /**
   * Fit the label into `maxWidth`: the width it will occupy, or null when it cannot
   * be shown at all there. (Names may come back shortened; counters are whole or null.)
   */
  fitLabel: (maxWidth: number) => number | null;
  note?: string;
  measureNote: (s: string) => number;
  /** Space kept between neighbouring pieces. */
  gap?: number;
  /** Narrowest a note may get before it is left out. */
  minNoteWidth?: number;
}

export interface InlineLayout {
  showStart: boolean;
  showEnd: boolean;
  /** The width to hand fitLabel again to get the lines to draw; null = no label. */
  labelMaxWidth: number | null;
  /** Center x of the label, or null if it has none. */
  labelCx: number | null;
  /** Note text to draw (possibly cut), its left x and width; null when there is no room. */
  note: { text: string; x: number; width: number } | null;
}

/**
 * One-line layout. The times are reserved FIRST and the label takes what is left —
 * a long name is shortened before a time is dropped, because the time is what says
 * where a block starts and ends (and the hover card carries the whole name). Only
 * when even a shortened label cannot fit is the end time, then the start time,
 * given up. The note gets whatever the label leaves over, and is left out rather
 * than displacing anything else.
 */
export function layoutInline(input: InlineInput): InlineLayout {
  const { x, width, note, measureNote } = input;
  const gap = input.gap ?? 3;
  const minNote = input.minNoteWidth ?? 14;
  const left = x + 2;
  const right = x + width - 2;

  // Give times up one at a time (end first) only if the label cannot be shown at all.
  const levels: Array<[number, number]> = [
    [input.startWidth, input.endWidth],
    [input.startWidth, 0],
    [0, 0],
  ];
  let chosen = { start: 0, end: 0, label: null as number | null, maxW: 0 };
  let prevKey = '';
  for (const [start, end] of levels) {
    const key = `${start}/${end}`;
    if (key === prevKey) continue;
    prevKey = key;
    const maxW = right - left - (start ? start + gap : 0) - (end ? end + gap : 0);
    const label = maxW > 0 ? input.fitLabel(maxW) : null;
    chosen = { start, end, label, maxW };
    if (label !== null) break;
  }

  const L = left + (chosen.start ? chosen.start + gap : 0);
  const R = right - (chosen.end ? chosen.end + gap : 0);
  const labelW = chosen.label ?? 0;

  let labelCx: number | null = null;
  let noteOut: InlineLayout['note'] = null;
  if (note) {
    const noteRoom = R - L - (labelW ? labelW + gap : 0);
    if (noteRoom >= minNote) {
      const text = ellipsize(note, noteRoom, measureNote);
      if (text) {
        const w = Math.min(measureNote(text), noteRoom);
        noteOut = { text, x: L, width: w };
        if (labelW) {
          const earliest = L + w + gap + labelW / 2;
          labelCx = Math.min(Math.max(x + width / 2, earliest), R - labelW / 2);
        }
      }
    }
  }
  if (labelW && labelCx === null) {
    labelCx = Math.min(Math.max(x + width / 2, L + labelW / 2), R - labelW / 2);
  }
  return {
    showStart: chosen.start > 0,
    showEnd: chosen.end > 0,
    labelMaxWidth: chosen.label !== null ? chosen.maxW : null,
    labelCx,
    note: noteOut,
  };
}

/** Biggest a note flag is drawn, and smallest worth drawing (a smaller triangle is a speck). */
export const NOTE_FLAG_MAX_PX = 6;
export const NOTE_FLAG_MIN_PX = 3;

/**
 * The corner flag that says "a note is saved here" when the note itself cannot be drawn as text.
 *
 * A compact row hands the note only what the times and label leave over, and leaves it out
 * rather than displacing either (see layoutInline) — so on a phone, with narrow cells, a saved
 * note simply could not be seen, and users took it as not saved. The flag sits in the top-right
 * corner (the spreadsheet comment marker), takes no room from anything, and the note is read by
 * opening the cell. Returns the triangle's three corners, or null when the cell is too small
 * to hold even a speck.
 */
export function noteFlag(
  x: number, y: number, width: number, height: number,
): Array<[number, number]> | null {
  const size = Math.min(NOTE_FLAG_MAX_PX, width, height);
  if (!(size >= NOTE_FLAG_MIN_PX)) return null;
  const right = x + width;
  return [[right - size, y], [right, y], [right, y + size]];
}
